import React, { useState, useMemo } from 'react';
import { AlertTriangle, TrendingDown, TrendingUp, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { isBuyDirection } from '../../../utils/tradeCalculations';

const PAGE_SIZE = 4;

const SEVERITY_STYLES = {
  high: 'bg-red-500/10 text-red-400 border-red-500/20',
  medium: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  low: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
};

const SEVERITY_DOT = {
  high: 'bg-red-500',
  medium: 'bg-yellow-500',
  low: 'bg-blue-500',
};

const RuleBreakingFeed = ({ data = [] }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [directionFilter, setDirectionFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [showFilters, setShowFilters] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const { availableTags, availableSymbols } = useMemo(() => {
    const tags = [...new Set(data.map(d => d.tag_label))].sort();
    const symbols = [...new Set(data.map(d => d.symbol))].sort();
    return { availableTags: tags, availableSymbols: symbols };
  }, [data]);

  const filteredAndSortedData = useMemo(() => {
    let filtered = [...data];

    if (selectedTags.length > 0) {
      filtered = filtered.filter(d => selectedTags.includes(d.tag_label));
    }
    if (selectedSymbols.length > 0) {
      filtered = filtered.filter(d => selectedSymbols.includes(d.symbol));
    }
    if (directionFilter !== 'all' && filtered.some(d => d.direction)) {
      if (directionFilter === 'buy' || directionFilter === 'long') {
        filtered = filtered.filter(d => d.direction && isBuyDirection(d.direction));
      } else if (directionFilter === 'sell' || directionFilter === 'short') {
        filtered = filtered.filter(d => d.direction && !isBuyDirection(d.direction));
      }
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc': return new Date(b.date) - new Date(a.date);
        case 'date-asc':  return new Date(a.date) - new Date(b.date);
        case 'severity': {
          const order = { high: 3, medium: 2, low: 1 };
          return (order[b.severity] || 0) - (order[a.severity] || 0);
        }
        case 'profit': return (b.profit || 0) - (a.profit || 0);
        default: return 0;
      }
    });

    return filtered;
  }, [data, selectedTags, selectedSymbols, directionFilter, sortBy]);

  const toggleTag = (tag) => setSelectedTags(prev =>
    prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
  );

  const toggleSymbol = (symbol) => setSelectedSymbols(prev =>
    prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol]
  );

  const clearFilters = () => {
    setSelectedTags([]);
    setSelectedSymbols([]);
    setDirectionFilter('all');
  };

  const hasActiveFilters = selectedTags.length > 0 || selectedSymbols.length > 0 || directionFilter !== 'all';
  const visibleData = expanded ? filteredAndSortedData : filteredAndSortedData.slice(0, PAGE_SIZE);
  const hiddenCount = filteredAndSortedData.length - PAGE_SIZE;

  if (data.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 self-start">
        <h3 className="text-sm font-semibold text-foreground mb-4">Rule-Breaking Feed</h3>
        <div className="flex flex-col items-center justify-center h-32 text-muted-foreground text-sm text-center">
          <p>No rule-breaking entries found.</p>
          <p className="text-xs mt-1">Keep following your rules -- you're doing great!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 self-start">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground">Rule-Breaking Feed</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {filteredAndSortedData.length} of {data.length}
          </span>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-1.5 rounded transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            title="Filter"
          >
            <Filter className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="mb-4 p-3 bg-muted/30 border border-border rounded-md space-y-3">
          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">Tags</label>
            <div className="flex flex-wrap gap-1.5">
              {availableTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded border transition-colors ${
                    selectedTags.includes(tag)
                      ? 'bg-primary/20 text-primary border-primary/40'
                      : 'bg-background text-muted-foreground border-border hover:border-foreground/20'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">Symbols</label>
            <div className="flex flex-wrap gap-1.5">
              {availableSymbols.map(symbol => (
                <button
                  key={symbol}
                  onClick={() => toggleSymbol(symbol)}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded border transition-colors ${
                    selectedSymbols.includes(symbol)
                      ? 'bg-primary/20 text-primary border-primary/40'
                      : 'bg-background text-muted-foreground border-border hover:border-foreground/20'
                  }`}
                >
                  {symbol}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">Direction</label>
            <div className="flex gap-1.5">
              {['all', 'buy', 'sell', 'long', 'short'].map(dir => (
                <button
                  key={dir}
                  onClick={() => setDirectionFilter(dir)}
                  className={`px-3 py-1 text-[10px] font-medium rounded border transition-colors ${
                    directionFilter === dir
                      ? 'bg-primary/20 text-primary border-primary/40'
                      : 'bg-background text-muted-foreground border-border hover:border-foreground/20'
                  }`}
                >
                  {dir.charAt(0).toUpperCase() + dir.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-foreground block mb-1.5">Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-2 py-1 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="date-desc">Date (Newest First)</option>
              <option value="date-asc">Date (Oldest First)</option>
              <option value="severity">Severity</option>
              <option value="profit">Profit/Loss</option>
            </select>
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-background border border-border rounded hover:bg-muted transition-colors"
            >
              <X className="w-3 h-3" />
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Feed */}
      {filteredAndSortedData.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 text-muted-foreground text-sm text-center">
          <p>No entries match your filters.</p>
          <button onClick={clearFilters} className="mt-2 text-xs text-primary hover:underline">
            Clear filters
          </button>
        </div>
      ) : (
        <>
          <div
            className={`space-y-3 pr-1 ${expanded ? 'overflow-y-auto max-h-[520px]' : ''}`}
          >
            {visibleData.map((card, idx) => (
              <div
                key={idx}
                className="bg-muted/30 border border-border rounded-md p-3 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {new Date(card.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span className="text-xs font-medium text-foreground">{card.symbol}</span>
                    {card.direction && (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        isBuyDirection(card.direction)
                          ? 'bg-[rgba(34,197,94,0.12)] text-[#4ADE80]'
                          : 'bg-[rgba(239,68,68,0.12)] text-[#F87171]'
                      }`}>
                        {card.direction.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${SEVERITY_DOT[card.severity] || SEVERITY_DOT.medium}`} />
                    <span className="text-[10px] text-muted-foreground capitalize">{card.severity}</span>
                  </div>
                </div>

                <div className="mb-2">
                  <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded border ${SEVERITY_STYLES[card.severity] || SEVERITY_STYLES.medium}`}>
                    {card.tag_label}
                  </span>
                </div>

                {card.phrase && (
                  <p className="text-xs text-muted-foreground italic mb-2 pl-2 border-l-2 border-border">
                    "{card.phrase}"
                  </p>
                )}

                {card.profit !== null && (
                  <div className="flex items-center gap-1 text-xs">
                    {card.profit >= 0
                      ? <TrendingUp className="w-3 h-3 text-[#4ADE80]" />
                      : <TrendingDown className="w-3 h-3 text-[#F87171]" />
                    }
                    <span className={`font-mono tabular-nums ${card.profit >= 0 ? 'text-[#4ADE80]' : 'text-[#F87171]'}`}>
                      {card.profit >= 0 ? '+' : ''}{card.profit.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredAndSortedData.length > PAGE_SIZE && (
            <button
              onClick={() => setExpanded(prev => !prev)}
              className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted/30 hover:bg-muted/50 border border-border rounded-md transition-colors"
            >
              {expanded ? (
                <><ChevronUp className="w-3.5 h-3.5" /> Show less</>
              ) : (
                <><ChevronDown className="w-3.5 h-3.5" /> Show {hiddenCount} more</>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default RuleBreakingFeed;
